import Icon from '__core/modules/atoms/Icons/Icon';
import useKeyboardNav from '__core/modules/hooks/useKeyboardNav';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import InputTextSkeleton from './InputTextSkeleton';

const DEFAULT_ON_CLICK_ICON = () => null;

const InputText = React.forwardRef(
  (
    {
      background = 'default',
      disabled = false,
      error,
      errorBorder,
      errorId,
      icon,
      iconColor = 'disabled',
      iconLeft = false,
      iconSize = 'small',
      isSkeleton = false,
      label,
      min,
      name,
      onChange = () => null,
      onClickIcon = DEFAULT_ON_CLICK_ICON,
      onKeyPress = () => {},
      placeholder,
      referer,
      suffix,
      type = 'text',
      variant = 'default',
      ...props
    },
    ref
  ) => {
    const refInput = referer || ref;
    const isIconClickable = onClickIcon !== DEFAULT_ON_CLICK_ICON;

    useKeyboardNav();

    function handleKeyDown(event) {
      if (min <= 0 && type === 'number' && event.key === '-') {
        event.preventDefault();
      }

      if ((event.key === 'Backspace' || event.key === 'Delete') && props.value === undefined) {
        onChange({ ...event, target: { ...event.target, value: '' } });
      }
    }

    if (isSkeleton) {
      return <InputTextSkeleton label={label} required={props.required} />;
    }

    const renderIcon = (content) =>
      isIconClickable ? (
        <button type="button" className="input-text__icon button-unstyled" onClick={onClickIcon}>
          {content}
        </button>
      ) : (
        <span className="input-text__icon input-text__icon--static">{content}</span>
      );

    return (
      <div
        className={clsx(
          'input-text',
          `input-text--${variant}`,
          `input-text--background-${background}`,
          { 'input-text--icon': !!icon || !!suffix },
          { 'input-text--icon-click': isIconClickable },
          { 'input-text--icon-left': iconLeft },
          { 'input-text--error': !!error },
          { 'input-text--error-border': errorBorder }
        )}
      >
        {label && (
          <label className="input-text__label" title={label}>
            {label} {props.required && <sup>*</sup>}
          </label>
        )}

        <div className="input-text__input-container">
          <input
            className={clsx('input-text__input', { 'input-text__input--disabled': disabled })}
            data-testid={name}
            disabled={disabled}
            min={min}
            name={name}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            onKeyUp={onKeyPress}
            placeholder={placeholder}
            ref={refInput}
            type={type}
            {...props}
          />

          {suffix && renderIcon(<label className="input-text__suffix">{suffix}</label>)}

          {icon && renderIcon(<Icon type={icon} size={iconSize} color={iconColor} />)}
        </div>
        {error && (
          <span className="input-text__error" id={errorId}>
            {error}
          </span>
        )}
      </div>
    );
  }
);

InputText.displayName = 'InputText';

InputText.propTypes = {
  background: PropTypes.oneOf(['default', 'grey']),
  disabled: PropTypes.bool,
  error: PropTypes.node,
  errorBorder: PropTypes.bool,
  // El mensaje de error no se asocia solo al input: quien lo necesite pasa el id y lo
  // referencia con aria-describedby (el atomo no usa htmlFor/id).
  errorId: PropTypes.string,
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  iconLeft: PropTypes.bool,
  iconSize: PropTypes.string,
  isSkeleton: PropTypes.bool,
  label: PropTypes.node,
  min: PropTypes.number,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onClickIcon: PropTypes.func,
  onKeyPress: PropTypes.func,
  placeholder: PropTypes.string,
  referer: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  required: PropTypes.bool,
  suffix: PropTypes.node,
  type: PropTypes.string,
  value: PropTypes.any,
  variant: PropTypes.oneOf(['default']),
};

export default InputText;
